import { css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ConditionEvaluator } from "./conditions/ConditionEvaluator";
import { TrackingElement } from "./TrackingElement";

@customElement("survaq-conditional-element")
class SurvaqConditionalElement extends TrackingElement {
  @property({ type: String }) condition = "";

  @state() private conditionMatched = false;

  private conditionEvaluator: ConditionEvaluator | null = null;
  private conditionDisposer: (() => void) | null = null;
  private iframeObserver: MutationObserver | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.setupIframeDeferral();
    this.setupConditionWatcher();
  }

  disconnectedCallback() {
    this.disposeIframeDeferral();
    this.disposeConditionWatcher();
    super.disconnectedCallback();
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has("condition")) {
      this.disposeConditionWatcher();
      this.setupConditionWatcher();
    }
  }

  private setupConditionWatcher() {
    if (!this.condition) {
      this.conditionMatched = true;
      return;
    }

    this.conditionEvaluator = new ConditionEvaluator();
    this.conditionDisposer = this.conditionEvaluator.watchCondition(this.condition, () =>
      this.onConditionMatched(),
    );
  }

  private disposeConditionWatcher() {
    this.conditionDisposer?.();
    this.conditionDisposer = null;
    this.conditionEvaluator?.dispose();
    this.conditionEvaluator = null;
  }

  private onConditionMatched() {
    this.conditionMatched = true;
    this.disposeIframeDeferral();
    this.activateDeferredIframes();
    this._fireTriggerEvent();
  }

  /**
   * 条件未成立の間、子要素の iframe が読み込まれるのを防ぐ。
   * iframe の src を data-deferred-src に退避し、条件成立時に復元する。
   * 埋めたページは読み込んだ時点で動き始める (クーポン面は 3 時間のカウントダウンを始める) ので、
   * 表示する前に読み込ませると、見る前に時間が進む。
   *
   * connectedCallback 時点で既に存在する iframe（React等）は同期的に処理し、
   * HTMLパーサーで後から追加される iframe は MutationObserver で捕捉する。
   */
  private setupIframeDeferral() {
    if (!this.condition) return;

    // 既に存在する iframe を即座に退避
    this.deferIframesIn(this);

    // 後から追加される iframe を監視
    this.iframeObserver = new MutationObserver(() => {
      if (this.conditionMatched) return;
      this.deferIframesIn(this);
    });
    this.iframeObserver.observe(this, { childList: true, subtree: true });
  }

  private disposeIframeDeferral() {
    this.iframeObserver?.disconnect();
    this.iframeObserver = null;
  }

  private deferIframesIn(root: Element) {
    const iframes = root.querySelectorAll<HTMLIFrameElement>("iframe[src]");
    for (const iframe of iframes) {
      iframe.dataset.deferredSrc = iframe.src;
      iframe.removeAttribute("src");
    }
  }

  private activateDeferredIframes() {
    const iframes = this.querySelectorAll<HTMLIFrameElement>("iframe[data-deferred-src]");
    for (const iframe of iframes) {
      iframe.src = iframe.dataset.deferredSrc ?? "";
      delete iframe.dataset.deferredSrc;
    }
  }

  private get shouldShow(): boolean {
    return this.conditionMatched;
  }

  render() {
    return html`
      ${this.renderTopMarker()}
      <div class="${this.shouldShow ? "block" : "hidden"}">
        <slot></slot>
      </div>
      ${this.renderBottomMarker()}
    `;
  }

  static styles = [
    ...TrackingElement.styles,
    css`
      .hidden {
        display: none;
      }
      .block {
        display: block;
      }
    `,
  ];
}

export { SurvaqConditionalElement };
